import { useEffect } from 'react';

/**
 * SEOHead Component
 * Manages meta tags, canonical URLs, robots directives, and structured data for SEO & AEO
 * 
 * @param {string} title - Page title
 * @param {string} description - Page description
 * @param {string} canonical - Canonical URL (defaults to clean current URL)
 * @param {object} jsonLd - JSON-LD structured data (optional)
 * @param {string} image - Open Graph image URL (optional)
 * @param {string} type - Open Graph type (default: 'website')
 * @param {boolean} noIndex - Whether to apply noindex, nofollow (default: false)
 */
export default function SEOHead({ 
  title, 
  description, 
  canonical, 
  jsonLd,
  image,
  type = 'website',
  noIndex = false,
}) {
  useEffect(() => {
    const canonicalUrl = canonical || (typeof window !== 'undefined' ? window.location.href.split('#')[0] : 'https://verveai.co');
    const defaultTitle = 'VerveAI — Turnkey Startup & Digital Launchpad | Powered by Verve AI Labs';
    let siteTitle = defaultTitle;
    if (title) {
      siteTitle = title.includes('VerveAI') || title.includes('VERVEAI')
        ? title
        : `${title} | VerveAI`;
    }
    const defaultDescription = 'Turnkey digital launchpad for newly incorporated businesses: official brand domains (.in/.com), high-speed web storefront, custom domain business email with SPF/DKIM deliverability, and 24/7 AI WhatsApp customer receptionists.';
    const metaDescription = description || defaultDescription;
    const defaultImage = 'https://verveai.co/logo.png';
    const ogImage = image || defaultImage;

    // Set document title
    document.title = siteTitle;

    // Helper function to set or update meta tags
    const setMetaTag = (attr, key, content) => {
      let element = document.querySelector(`meta[${attr}="${key}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attr, key);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Helper function to set or update link tags
    const setLinkTag = (rel, href) => {
      let element = document.querySelector(`link[rel="${rel}"]`);
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.setAttribute('href', href);
    };

    // Primary Meta Tags
    setMetaTag('name', 'title', siteTitle);
    setMetaTag('name', 'description', metaDescription);
    if (noIndex) {
      setMetaTag('name', 'robots', 'noindex, nofollow');
    } else {
      setMetaTag('name', 'robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');
    }
    
    // Canonical URL
    if (canonicalUrl) {
      setLinkTag('canonical', canonicalUrl);
    }
    
    // Open Graph / Facebook
    setMetaTag('property', 'og:type', type);
    setMetaTag('property', 'og:url', canonicalUrl);
    setMetaTag('property', 'og:title', siteTitle);
    setMetaTag('property', 'og:description', metaDescription);
    setMetaTag('property', 'og:image', ogImage);
    setMetaTag('property', 'og:site_name', 'VerveAI');
    
    // Twitter
    setMetaTag('property', 'twitter:card', 'summary_large_image');
    setMetaTag('property', 'twitter:url', canonicalUrl);
    setMetaTag('property', 'twitter:title', siteTitle);
    setMetaTag('property', 'twitter:description', metaDescription);
    setMetaTag('property', 'twitter:image', ogImage);
    
    // JSON-LD Structured Data
    if (jsonLd) {
      let script = document.querySelector('script[data-dynamic-jsonld="true"]');
      if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-dynamic-jsonld', 'true');
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    }

    return () => {
      // Clean up dynamic jsonld on unmount
      const script = document.querySelector('script[data-dynamic-jsonld="true"]');
      if (script && script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [title, description, canonical, jsonLd, image, type, noIndex]);

  return null;
}
